/**
 * REL-24 — the shell → server bridge for StoreKit purchases.
 *
 * Same discipline as `lib/health/healthKit.ts` and `lib/run/liveActivity.ts`:
 * native-only, bound by jsName through `@capacitor/core` so the WEB bundle
 * never depends on the plugin package, and no path throws — a billing helper
 * that throws would take the Settings page down with it.
 *
 * THE TRUST BOUNDARY. Nothing in this module decides entitlement. It moves
 * signed transactions from StoreKit to `POST /api/storekit/sync` and returns
 * whatever the SERVER says. `entitled` in the result is the server's word.
 */

import { isNativeApp } from '../platform';
import { STOREKIT_PRODUCT_IDS } from './storeKitProducts';
import type { AuthFetchFn } from '../../hooks/useAuthFetch';

export interface StoreKitProduct {
  id: string;
  displayName: string;
  description: string;
  /** Apple's LOCALIZED, storefront-correct price string. Never computed here. */
  displayPrice: string;
  currencyCode?: string;
  periodValue?: number;
  periodUnit?: 'day' | 'week' | 'month' | 'year' | 'unknown';
}

export interface StoreKitPlugin {
  isAvailable(): Promise<{ available: boolean }>;
  getProducts(options: { productIds: readonly string[] }): Promise<{ products: StoreKitProduct[] }>;
  purchase(options: { productId: string }): Promise<{
    status: 'success' | 'pending' | 'cancelled' | 'unknown';
    jws?: string;
    transactionId?: string;
  }>;
  currentEntitlements(): Promise<{ transactions: string[] }>;
  restore(): Promise<{ transactions: string[]; syncError?: string }>;
  finishTransaction(options: { transactionId: string }): Promise<{ finished: boolean }>;
  addListener(
    event: 'transactionUpdate',
    handler: (data: { jws: string }) => void,
  ): Promise<{ remove: () => Promise<void> }>;
}

/** The server's answer, normalized. `entitled` is the ONLY entitlement signal. */
export interface EntitlementState {
  entitled: boolean;
  tier?: string | null;
  productId?: string | null;
  expiresAt?: string | null;
  environment?: string | null;
  downgraded?: boolean;
  /** Set when the server refused: e.g. entitlement_owned_by_another_account. */
  code?: string | null;
  error?: string | null;
}

let boundPlugin: StoreKitPlugin | null = null;

/** Bind (once) and return the plugin proxy; null off-native or on bind failure.
 *  Wrapped in an object per the Live Activity thenable lesson. */
export async function getStoreKitPlugin(): Promise<{ sk: StoreKitPlugin } | null> {
  if (!isNativeApp()) return null;
  if (boundPlugin) return { sk: boundPlugin };
  try {
    const { registerPlugin } = await import('@capacitor/core');
    boundPlugin = registerPlugin<StoreKitPlugin>('TayroStoreKit');
    return { sk: boundPlugin };
  } catch (err) {
    console.warn('[StoreKit] plugin bind failed:', err instanceof Error ? err.message : String(err));
    return null;
  }
}

/** Purchases disabled by Screen Time / parental controls / MDM ⇒ no buy row. */
export async function canMakePayments(): Promise<boolean> {
  const p = await getStoreKitPlugin();
  if (!p) return false;
  try {
    const res = await p.sk.isAvailable();
    return res?.available === true;
  } catch {
    return false;
  }
}

/** The two ruled products, with Apple's localized prices. Empty on any failure
 *  — the surface then shows its "not available right now" state rather than a
 *  button that cannot work. */
export async function loadProducts(): Promise<StoreKitProduct[]> {
  const p = await getStoreKitPlugin();
  if (!p) return [];
  try {
    const res = await p.sk.getProducts({ productIds: STOREKIT_PRODUCT_IDS });
    return Array.isArray(res?.products) ? res.products : [];
  } catch (err) {
    console.warn('[StoreKit] product load failed:', err instanceof Error ? err.message : String(err));
    return [];
  }
}

/**
 * Post signed transactions to the server and return ITS verdict.
 * `reason` is telemetry; the server never grants anything on its word.
 */
export async function syncEntitlement(
  fetchAuthed: AuthFetchFn,
  transactions: string[],
  reason: 'purchase' | 'restore' | 'launch' | 'update',
): Promise<EntitlementState> {
  try {
    const res = await fetchAuthed('/api/storekit/sync', {
      method: 'POST',
      body: JSON.stringify({ transactions, reason }),
    });
    const json = (await res.json().catch(() => ({}))) as Partial<EntitlementState>;
    if (!res.ok) {
      return {
        entitled: false,
        code: json?.code ?? null,
        error: (json as { error?: string })?.error ?? null,
      };
    }
    return {
      entitled: json?.entitled === true,
      tier: json?.tier ?? null,
      productId: json?.productId ?? null,
      expiresAt: json?.expiresAt ?? null,
      environment: json?.environment ?? null,
      downgraded: json?.downgraded === true,
      code: null,
      error: null,
    };
  } catch (err) {
    console.warn('[StoreKit] sync failed:', err instanceof Error ? err.message : String(err));
    return { entitled: false, code: 'network', error: null };
  }
}

/** Launch / foreground reconcile: read current entitlements, let the server
 *  decide. Fire-and-forget, silent on every failure path. */
export async function syncCurrentEntitlements(
  fetchAuthed: AuthFetchFn,
  reason: 'launch' | 'update' = 'launch',
): Promise<EntitlementState | null> {
  const p = await getStoreKitPlugin();
  if (!p) return null;
  try {
    const res = await p.sk.currentEntitlements();
    const transactions = Array.isArray(res?.transactions) ? res.transactions : [];
    return await syncEntitlement(fetchAuthed, transactions, reason);
  } catch (err) {
    console.warn('[StoreKit] entitlement read failed:', err instanceof Error ? err.message : String(err));
    return null;
  }
}

export type PurchaseOutcome =
  | { status: 'success'; state: EntitlementState }
  | { status: 'pending' }
  | { status: 'cancelled' }
  | { status: 'unavailable' }
  | { status: 'failed'; message: string | null };

/**
 * Buy, then verify SERVER-SIDE, then finish the transaction.
 *
 * The order is load-bearing. Finishing before the server stamps would discard a
 * transaction we had not been credited for; while it stays unfinished StoreKit
 * keeps returning it, so a failed sync repairs itself at the next launch.
 */
export async function purchaseProduct(
  fetchAuthed: AuthFetchFn,
  productId: string,
): Promise<PurchaseOutcome> {
  const p = await getStoreKitPlugin();
  if (!p) return { status: 'unavailable' };
  let result: Awaited<ReturnType<StoreKitPlugin['purchase']>>;
  try {
    result = await p.sk.purchase({ productId });
  } catch (err) {
    return { status: 'failed', message: err instanceof Error ? err.message : null };
  }

  if (result?.status === 'cancelled') return { status: 'cancelled' };
  // Ask to Buy and deferred payments land here; the real transaction arrives
  // later on `transactionUpdate`.
  if (result?.status === 'pending') return { status: 'pending' };
  if (result?.status !== 'success' || !result.jws) {
    return { status: 'failed', message: null };
  }

  const state = await syncEntitlement(fetchAuthed, [result.jws], 'purchase');
  if (state.entitled && result.transactionId) {
    try {
      await p.sk.finishTransaction({ transactionId: result.transactionId });
    } catch {
      /* left unfinished on purpose — the next launch sync closes it out */
    }
  }
  return { status: 'success', state };
}

export type RestoreOutcome =
  | { status: 'restored'; state: EntitlementState }
  | { status: 'nothing' }
  | { status: 'unavailable' };

/**
 * Restore Purchases — an App Review requirement for any app with a
 * non-consumable or auto-renewable purchase.
 *
 * `AppStore.sync()` runs inside the plugin; a cancelled sign-in sheet still
 * returns whatever entitlements are known, so the control is never a dead end.
 */
export async function restorePurchases(fetchAuthed: AuthFetchFn): Promise<RestoreOutcome> {
  const p = await getStoreKitPlugin();
  if (!p) return { status: 'unavailable' };
  try {
    const res = await p.sk.restore();
    const transactions = Array.isArray(res?.transactions) ? res.transactions : [];
    const state = await syncEntitlement(fetchAuthed, transactions, 'restore');
    return state.entitled ? { status: 'restored', state } : { status: 'nothing' };
  } catch (err) {
    console.warn('[StoreKit] restore failed:', err instanceof Error ? err.message : String(err));
    return { status: 'nothing' };
  }
}

/** Subscribe to `Transaction.updates` (Ask to Buy approvals, renewals, changes
 *  made on another device). Returns a detach function; no-op off native. */
export async function onTransactionUpdate(
  handler: (jws: string) => void,
): Promise<() => void> {
  const p = await getStoreKitPlugin();
  if (!p) return () => {};
  try {
    const handle = await p.sk.addListener('transactionUpdate', (data) => {
      if (data && typeof data.jws === 'string') handler(data.jws);
    });
    return () => void handle.remove();
  } catch {
    return () => {};
  }
}
